import { CopySVG, DownloadArrowSVG, EditSVG } from 'src/components/Icons/Basic'
export {
  GapAccordion as ControlCheckTestAccordion,
  GapAccordionItem as ControlCheckTestAccordionItem,
  GapAccordionHeader as ControlCheckTestAccordionHeader,
  GapAccordionBody as ControlCheckTestAccordionBody,
  GapRemediationContainer as ControlGapRemediationContainer,
  GapRemediationItem as ControlGapRemediationItem,
  RemediationIcon as ControlRemediationIcon,
} from 'src/components/Reusable/GapRecommendations/GapRecommendations.styles'
import {
  EroConcernedEastSVG,
  EroDarkEastSVG,
  UserEditedImg,
  UserEditedWhiteImg,
} from 'src/components/Icons/Brand'
import { FlexRow } from 'src/components/Reusable/Flex'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { SectionTitle } from 'src/components/Reusable/Text/Text.styles'
import palette from 'src/designSystem/variables/palette'
import styled, { keyframes } from 'styled-components/macro'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

// Fades content in when it mounts — used to animate Gaps and Recommendations
// appearing as a control finalizes.
export const FadeIn = styled.div`
  animation: ${fadeIn} 0.4s ease;
`

export const ScanAgainContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`

export const ControlCheckEroConcernedIcon = styled(EroConcernedEastSVG)`
  min-height: 3em;
  min-width: 3em;
  height: 3em;
  width: 3em;
`

export const ControlCheckEroIcon = styled(EroDarkEastSVG)`
  min-height: 3em;
  min-width: 3em;
  height: 3em;
  width: 3em;
`

export const ControlCheckCopyMenuDropdown = styled.div`
  .dropdown-toggle {
    max-height: min-content;
    &::after {
      display: none;
    }
    &:hover {
      svg path {
        fill: ${palette.neutral[300]};
      }
      background-color: unset;
    }
  }
`

export const CopyMenuDropdown = styled.div`
  border-radius: 16px;
  background-color: white;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: none;
  padding: 6px;
  margin-top: 8px;
  position: absolute;
  z-index: 1000;
`

export const CopyMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 12px;
  color: ${palette.neutral[900]};
  white-space: nowrap;

  &:hover {
    background-color: ${palette.neutral[50]};
  }
`

export const SuccessfulControlTests = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 500;
  font-color: ${palette.neutral[900]};
  background-color: ${palette.orange[30]};
  width: fit-content;
  margin-top: 20px;
`

export const UserEditedControlTestIcon = styled(UserEditedImg)`
  width: 2em;
  height: 2em;
`

export const UserEditedControlTestLightIcon = styled(UserEditedWhiteImg)`
  width: 2em;
  height: 2em;
`

export const UserEditedControlTestContainer = styled.div<{
  $backgroundColorDark?: boolean
}>`
  background-color: ${({ $backgroundColorDark }) =>
    $backgroundColorDark ? palette.neutral[600] : 'inherit'};

  flex-direction: column;
  border-radius: 50%;

  display: flex;
  height: 3em;
  width: 3em;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

export const ControlCheckDateContainer = styled.div`
  font-weight: 500;
`

export const ControlCheckDescriptionRow = styled.div`
  grid-column: 2 / -1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`
export const ControlCheckButtonGroup = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  text-align: center;
  opacity: ${({ $isVisible = true }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible = true }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 0.15s ease;
`

export const ControlCheckHeaderButtonsWrapper = styled.div`
  margin-left: auto;
  margin-right: 12px;
`

export const ControlCheckResultContainer = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 8px;
  margin-bottom: 4px;
  width: 100%;
`

export const ControlCheckSummaryTitle = styled(SectionTitle).attrs({
  $size: 14,
})`
  grid-column: 2 / -1;
  font-size: 14px;
  font-weight: 500;
`

export const ControlCheckTestsTitle = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 500;
`

export const ControlCheckEroText = styled.div<{ $isItalic?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-column: 2 / -1;
  gap: 4px;
  font-style: ${({ $isItalic }) => ($isItalic ? 'italic' : 'normal')};
  .markup-section > p {
    margin: 0;
  }
`

export const EditedResultSection = styled.div`
  font-size: 16px;
  font-style: italic;
  padding: 0 6px;
  margin: 0;
  color: ${palette.orange[900]};
`

export const ControlCheckMarkup = styled(Markup)`
  &.markup-section {
    padding: 0;
  }
`

export const ControlCheckSourcesContainer = styled.div<{
  $isFullWidth?: boolean
}>`
  margin-bottom: 4px;
  margin-left: 24px;
  ${({ $isFullWidth }) =>
    $isFullWidth &&
    `
    grid-column: 1 / -1;
  `}
`

export const ControlCheckTestProcedureContainer = styled.div<{
  $isFail?: boolean
  $isFullWidth?: boolean
}>`
  grid-column: 2 / -1;
  display: grid;
  gap: 8px;
  grid-template-columns: 16px 16px 1fr;
  margin-bottom: 4px;
  padding-right: 16px;
  ${({ $isFail }) =>
    $isFail &&
    `
    border-right: 4px solid ${palette.magenta[200]};
  `}
  ${({ $isFullWidth }) =>
    $isFullWidth &&
    `
    grid-column: 1 / -1;
  `}
`

export const ControlCheckTestProcedureIcon = styled.span`
  height: 16px;
  align-items: center;
`

export const ControlCheckTestProcedureNumber = styled.span`
  font-weight: 500;
`

export const ControlCheckTestProcedureDescription = styled.span<{
  $showNumber?: boolean
}>`
  ${({ $showNumber }) =>
    !$showNumber &&
    `
      grid-column: 2 / -1;
    `}
  font-weight: 500;
  .markup-section {
    margin: 0;
  }
`
export const ControlCheckTestProcedureBody = styled.div<{
  $showNumber?: boolean
}>`
  ${({ $showNumber }) =>
    $showNumber ? `grid-column: 3 / -1;` : `grid-column: 2 / -1;`}
  .markup-section {
    margin-bottom: 16px;
    p {
      margin: 0;
    }
  }
`

export const TestProcedureEvidenceSourcesContainer = styled.div`
  color: ${palette.neutral[600]};
  display: flex;
  flex-direction: column;
  gap: 8px;
  line-height: 150%;
`

export const ControlCheckEditIcon = styled(EditSVG)`
  path {
    fill: ${palette.neutral[600]};
  }
  width: 16px;
  height: 16px;
`

export const MarkAsReviewedButton = styled.button`
  background: none;
  border: none;
  color: ${palette.blue[600]};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  letter-spacing: 0.5px;
  white-space: nowrap;
`

export const ReviewedCountSubtle = styled.span`
  color: ${palette.neutral[500]};
  margin-left: 4px;
`

export const ControlCheckCopyIcon = styled(CopySVG)`
  path {
    fill: ${palette.neutral[600]};
  }
  width: 16px;
  height: 16px;
`

export const ControlCheckExportIcon = styled(DownloadArrowSVG)`
  width: 16px;
  height: 16px;
`

export const ControlCheckSourceTestTableButton = styled.div`
  width: fit-content;
  justify-content: flex-start;
  margin-left: 24px;
`

export const ControlCheckSourceResultMismatchRow = styled(FlexRow)`
  margin-left: 24px;
`

export const ControlCheckSourceResultMismatchContainer = styled.div`
  svg {
    height: 20px;
    fill: ${palette.purple[600]};
  }
  background-color: ${palette.purple[30]};
  color: ${palette.purple[600]};
  align-items: center;
  font-size: 16px;
  display: flex;
  gap: 8px;
  border-radius: 6px;
  padding: 8px 12px;
  width: fit-content;
`

export const ControlCheckSourceResultMismatchLink = styled.a`
  color: ${palette.purple[600]};
  text-decoration: none;
  font-weight: 500;
  width: fit-content;
}
  `

export const ReasonResultsEditedTitle = styled(SectionTitle).attrs({
  $size: 14,
})`
  grid-column: 2 / -1;
  font-size: 14px;
  font-weight: 500;
`
export const EditReviewInfoContainer = styled.div`
  grid-column: 2 / -1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const EditReviewInfoText = styled.div`
  grid-column: 2 / -1;
  font-size: 13px;
  font-style: italic;
  color: ${palette.neutral[600]};
`

/** Wraps edit/review info in Bootstrap tooltips (tooltip-inner is centered by default). */
export const EditReviewTooltipBody = styled.div`
  text-align: left;
  font-size: 12px;
  line-height: 1.45;
  gap: 8px;
  display: flex;
  flex-direction: column;
`
